import React from 'react'
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom'

import { getSessionToken, SESSION_EXPIRED_EVENT } from './auth/session'
import Login from './routes/Login'
import Packs from './routes/Packs'
import OrgSetup from './routes/OrgSetup'
import Onboarding from './routes/Onboarding'
import Watchlists from './routes/Watchlists'
import Watchlist from './routes/Watchlist'
import Feed from './routes/Feed'
import ItemDetail from './routes/ItemDetail'
import Companies from './routes/Companies'
import CompanyDetail from './routes/CompanyDetail'
import CompanyGroup from './routes/CompanyGroup'
import MyQuestions from './routes/MyQuestions'
import QuestionSetDetail from './routes/QuestionSetDetail'
import Scenarios from './routes/Scenarios'
import ScenarioDetail from './routes/ScenarioDetail'
import Workspaces from './routes/Workspaces'
import WorkspaceNew from './routes/WorkspaceNew'
import WorkspaceDetail from './routes/WorkspaceDetail'
import Universe from './routes/Universe'

function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = getSessionToken()
  if (!token) return <Navigate to="/login" replace />
  return <>{children}</>
}

export default function App() {
  return (
    <>
      <SessionExpiredRedirect />
      <Routes>
      <Route path="/login" element={<Login />} />
      {/* Workspaces is the home of the product. The old /dashboard summary now
          lives as a KPI strip atop the Workspaces list, so legacy /dashboard
          links redirect there. */}
      <Route path="/dashboard" element={<Navigate to="/workspaces" replace />} />
      <Route
        path="/onboarding"
        element={
          <RequireAuth>
            <Onboarding />
          </RequireAuth>
        }
      />
      <Route
        path="/org-setup"
        element={
          <RequireAuth>
            <OrgSetup />
          </RequireAuth>
        }
      />
      <Route
        path="/packs"
        element={
          <RequireAuth>
            <Packs />
          </RequireAuth>
        }
      />
      <Route
        path="/watchlists"
        element={
          <RequireAuth>
            <Watchlists />
          </RequireAuth>
        }
      />
      <Route
        path="/watchlists/:watchlistId"
        element={
          <RequireAuth>
            <Watchlist />
          </RequireAuth>
        }
      />
      <Route
        path="/watchlists/:watchlistId/feed"
        element={
          <RequireAuth>
            <Feed />
          </RequireAuth>
        }
      />
      <Route
        path="/items/:itemId"
        element={
          <RequireAuth>
            <ItemDetail />
          </RequireAuth>
        }
      />
      <Route
        path="/my-questions"
        element={
          <RequireAuth>
            <MyQuestions />
          </RequireAuth>
        }
      />
      <Route
        path="/question-sets/:setId"
        element={
          <RequireAuth>
            <QuestionSetDetail />
          </RequireAuth>
        }
      />
      {/* Universe — primary section for reference entities (Companies + Scenarios).
          Canonical paths live under /universe/*. Legacy /companies/* and
          /scenarios/* paths below redirect into Universe so external links
          and old bookmarks keep working. */}
      <Route
        path="/universe"
        element={
          <RequireAuth>
            <Universe />
          </RequireAuth>
        }
      />
      <Route
        path="/universe/companies"
        element={
          <RequireAuth>
            <Companies />
          </RequireAuth>
        }
      />
      <Route
        path="/universe/companies/groups/:groupId"
        element={
          <RequireAuth>
            <CompanyGroup />
          </RequireAuth>
        }
      />
      <Route
        path="/universe/companies/:companyId"
        element={
          <RequireAuth>
            <CompanyDetail />
          </RequireAuth>
        }
      />
      <Route
        path="/universe/scenarios"
        element={
          <RequireAuth>
            <Scenarios />
          </RequireAuth>
        }
      />
      <Route
        path="/universe/scenarios/:scenarioId"
        element={
          <RequireAuth>
            <ScenarioDetail />
          </RequireAuth>
        }
      />

      {/* Legacy redirects so existing /companies and /scenarios links keep
          working. `replace` keeps history clean. */}
      <Route path="/companies" element={<Navigate to="/universe/companies" replace />} />
      <Route
        path="/companies/groups/:groupId"
        element={<LegacyCompanyGroupRedirect />}
      />
      <Route
        path="/companies/:companyId"
        element={<LegacyCompanyRedirect />}
      />
      <Route
        path="/workspaces"
        element={
          <RequireAuth>
            <Workspaces />
          </RequireAuth>
        }
      />
      <Route
        path="/workspaces/new"
        element={
          <RequireAuth>
            <WorkspaceNew />
          </RequireAuth>
        }
      />
      <Route
        path="/workspaces/:workspaceId"
        element={
          <RequireAuth>
            <WorkspaceDetail />
          </RequireAuth>
        }
      />
      <Route path="/scenarios" element={<Navigate to="/universe/scenarios" replace />} />
      <Route path="/scenarios/:scenarioId" element={<LegacyScenarioRedirect />} />

      {/* Workspaces is the primary entry point of the product. */}
      <Route path="/" element={<Navigate to="/workspaces" replace />} />
      <Route path="*" element={<Navigate to="/workspaces" replace />} />
      </Routes>
    </>
  )
}

function SessionExpiredRedirect() {
  const nav = useNavigate()
  React.useEffect(() => {
    const onExpired = () => {
      if (window.location.pathname !== '/login') nav('/login', { replace: true })
    }
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired)
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired)
  }, [nav])
  return null
}

function LegacyCompanyRedirect() {
  const { companyId } = useParams<'companyId'>()
  return <Navigate to={`/universe/companies/${companyId ?? ''}`} replace />
}

function LegacyCompanyGroupRedirect() {
  const { groupId } = useParams<'groupId'>()
  return <Navigate to={`/universe/companies/groups/${groupId ?? ''}`} replace />
}

function LegacyScenarioRedirect() {
  const { scenarioId } = useParams<'scenarioId'>()
  return <Navigate to={`/universe/scenarios/${scenarioId ?? ''}`} replace />
}
